import Input from "../../shared/Input";
import Select from "../../shared/Select";
import Toggle from "../../shared/Toggle";
import { stateOptions } from "../../constants/select";
import {
  CDL_CLASS_OPTIONS,
  CDL_ENDORSEMENT_OPTIONS,
  DRIVER_EMPLOYMENT_STATUS_OPTIONS,
  DRIVER_FORM_SIZE,
  DRIVER_PAY_TYPE_OPTIONS,
} from "../../constants/driver-profile";
import { parseEndorsements } from "../../utils/driver-profile.utils";

/**
 * Trucking TMS driver compliance + pay fields shared by create/edit flows.
 *
 * @param {{form:Object, updateForm:Function, size?:string}} props
 */
const DriverComplianceSection = ({
  form = {},
  updateForm,
  size = DRIVER_FORM_SIZE,
}) => {
  const selectedEndorsements = parseEndorsements(form.endorsements);

  function handleFieldChange(event) {
    updateForm(event);
  }

  function handleToggleChange(id, checked) {
    updateForm({ id, value: checked });
  }

  function toggleEndorsement(code) {
    const next = selectedEndorsements.includes(code)
      ? selectedEndorsements.filter((entry) => entry !== code)
      : [...selectedEndorsements, code];

    updateForm({ id: "endorsements", value: next });
  }

  return (
    <div className="space-y-6">
      <section className="space-y-4 rounded-lg border border-gray-200 p-4">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
            CDL & Credentials
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Track license, medical certificate, and hazmat qualification per DOT
            driver qualification file standards.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            id="licenseNumber"
            label="CDL Number"
            placeholder="CDL number"
            value={form.licenseNumber || form.license || ""}
            onChange={handleFieldChange}
            size={size}
          />
          <Select
            id="licenseState"
            label="License State"
            placeholder="Select state"
            value={form.licenseState}
            options={stateOptions}
            onChange={handleFieldChange}
            size={size}
          />
          <Select
            id="licenseType"
            label="CDL Class"
            value={form.licenseType}
            options={CDL_CLASS_OPTIONS}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="licenseExpiration"
            label="CDL Expiration"
            type="date"
            value={form.licenseExpiration || ""}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="medicalCertExpiration"
            label="Medical Certificate Expiration"
            type="date"
            value={form.medicalCertExpiration || ""}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="yearsOfExperience"
            label="Years of Experience"
            type="number"
            min="0"
            value={form.yearsOfExperience || ""}
            onChange={handleFieldChange}
            size={size}
          />
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-gray-900">Endorsements</p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {CDL_ENDORSEMENT_OPTIONS.map(({ code, label }) => (
              <label
                key={code}
                className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={selectedEndorsements.includes(code)}
                  onChange={() => toggleEndorsement(code)}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
            <span className="text-sm font-medium text-gray-700">
              Hazmat Certified
            </span>
            <Toggle
              id="hazmatCertified"
              size={size}
              checked={!!form.hazmatCertified}
              onChange={(event) =>
                handleToggleChange("hazmatCertified", event.target.checked)
              }
            />
          </div>
          <Input
            id="hazmatCertExpiration"
            label="Hazmat Cert Expiration"
            type="date"
            value={form.hazmatCertExpiration || ""}
            onChange={handleFieldChange}
            size={size}
            disabled={!form.hazmatCertified}
          />
          <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
            <span className="text-sm font-medium text-gray-700">TWIC Card</span>
            <Toggle
              id="twicCertified"
              size={size}
              checked={!!form.twicCertified}
              onChange={(event) =>
                handleToggleChange("twicCertified", event.target.checked)
              }
            />
          </div>
          <Input
            id="twicExpiration"
            label="TWIC Expiration"
            type="date"
            value={form.twicExpiration || ""}
            onChange={handleFieldChange}
            size={size}
            disabled={!form.twicCertified}
          />
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-gray-200 p-4">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
            Employment & Pay
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Home terminal, employment status, and default pay settings used on
            loads, legs, and payouts.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Select
            id="employmentStatus"
            label="Employment Status"
            value={form.employmentStatus}
            options={DRIVER_EMPLOYMENT_STATUS_OPTIONS}
            onChange={handleFieldChange}
            size={size}
          />
          <Select
            id="payType"
            label="Pay Type"
            value={form.payType}
            options={DRIVER_PAY_TYPE_OPTIONS}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="homeTerminal"
            label="Home Terminal"
            placeholder="City, terminal, or yard"
            value={form.homeTerminal || ""}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="hourlyRate"
            label="Hourly Rate"
            type="number"
            step="0.01"
            min="0"
            value={form.hourlyRate || ""}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="mileageRate"
            label="Mileage Rate"
            type="number"
            step="0.01"
            min="0"
            value={form.mileageRate || ""}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="emptyRate"
            label="Empty Rate"
            type="number"
            step="0.01"
            min="0"
            value={form.emptyRate || ""}
            onChange={handleFieldChange}
            size={size}
          />
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-gray-200 p-4">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
            Compliance Testing & Emergency Contact
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            id="lastDrugTestDate"
            label="Last Drug Test Date"
            type="date"
            value={form.lastDrugTestDate || ""}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="nextDrugTestDue"
            label="Next Drug Test Due"
            type="date"
            value={form.nextDrugTestDue || ""}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="lastMvrDate"
            label="Last MVR Review Date"
            type="date"
            value={form.lastMvrDate || ""}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="emergencyContactName"
            label="Emergency Contact Name"
            value={form.emergencyContactName || ""}
            onChange={handleFieldChange}
            size={size}
          />
          <Input
            id="emergencyContactPhone"
            label="Emergency Contact Phone"
            type="tel"
            value={form.emergencyContactPhone || ""}
            onChange={handleFieldChange}
            size={size}
          />
        </div>
      </section>
    </div>
  );
};

export default DriverComplianceSection;
